import { App, Form, Modal } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useQuarantineDefectV1ControllerUpdateRepairDetails } from 'generated/apiComponents';
import { RepairOutputDto } from 'generated/apiSchemas';
import { Module_Enum, useGetDefectRepairOptionsQuery } from 'generated/graphql';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

import { UpdateRepairFormValues } from './types';
import { UpdateRepairForm } from './update-repair-form';

interface UpdateRepairModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  repair: RepairOutputDto | null;
  refetchData: () => void;
}

export const UpdateRepairModal: React.FC<UpdateRepairModalProps> = ({
  isModalVisible,
  closeModal,
  repair,
  refetchData,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UpdateRepairFormValues>();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite');

  const { mutate: updateDefectRepair } =
    useQuarantineDefectV1ControllerUpdateRepairDetails();

  const save = async (row: UpdateRepairFormValues) => {
    updateDefectRepair(
      {
        pathParams: {
          defectId: repair?.quarantineDefectId ?? '',
        },
        body: {
          type: row.type,
          date: row.date.utc().toISOString(),
          description: row.description,
        },
      },
      {
        onSuccess: async () => {
          refetchData();
          closeModal();

          message.open({
            type: 'success',
            content: t(
              'qualityControl.segmentHistory.drawer.repairModal.update.success',
            ),
          });
        },
        onError: () => {
          message.open({
            type: 'error',
            content: t(
              'qualityControl.segmentHistory.drawer.repairModal.update.error',
            ),
          });
        },
      },
    );
  };

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data, loading } = useGetDefectRepairOptionsQuery({
    variables: { projectId: currentConstructionSite.projectId },
    context: { headers: graphqlHeaders },
  });

  const typeOptions = useMemo(() => {
    const options =
      data?.defectTypes?.map((type) => ({
        label: type.label,
        value: type.id,
      })) || [];

    return options;
  }, [data]);

  const currentType = typeOptions.find((type) => type.label === repair?.type);

  const initialRepairValues = {
    description: repair?.description ?? '',
    date: dayjs(repair?.createdAt).tz().second(0).millisecond(0),
    type: currentType?.value,
  };

  return (
    <Modal
      title={t('qualityControl.segmentHistory.drawer.repairModal.title')}
      open={isModalVisible}
      onOk={form.submit}
      onCancel={closeModal}
      destroyOnHidden
    >
      {!isUndefinedOrNull(repair) ? (
        <UpdateRepairForm
          form={form}
          save={save}
          initialValues={initialRepairValues}
          typeOptions={typeOptions}
          loading={loading}
        />
      ) : null}
    </Modal>
  );
};
